import React, { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { Modal, Button } from '../common';
import { closeProject, fetchAllProjectProblems } from './ocrApi';
import { closeStageCounts, closeMode, classifyClose409 } from './closeStage';

/**
 * 종료 = 최종 시험지 생성 (⑦ 프로젝트 종료).
 *
 * **서버가 시험지를 만든다.** `POST /close` 가 승격된 문제 전부를 교재 순서(scope·번호)로 담아
 * 시험지 1장을 만들고 status 500 까지 한 트랜잭션으로 처리한다
 * (BE-20260830-reply-ocr-repromote-history-close.md §4, 오너 결정 ③).
 * 2026-08-30 이전의 프론트 시험지 조립(라벨 되찾기·건수 대조·문항 빼기)을 되살리면 **시험지가 두 장** 생긴다.
 *
 * ── 2026-09-25 개편(BE-20260925 F12 · 오너 결정 「N건 등록 창 열기」) ──
 * prod #35 는 22문제를 검수·분석하고 ⑥ 없이 이 창에서 닫혔다. 종전 창은 등록 0건이어도
 * 「시험지로 저장하고 종료」라고 말했고, 서버 409 를 받은 뒤에야 개발자 말(force=true)로 되물었다.
 * 이제 **창을 여는 순간 무엇이 남았는지 센다**(closeStage — 서버 종료 가드와 같은 규칙) —
 *   · 등록할 것이 남음 → 주 버튼 「N건 등록 창 열기」(⑥ 의 등록 창을 연다 — 공통 키워드 확인을 건너뛰지 않는다),
 *     버리고 닫기는 약한 버튼 + 한 번 더 확인
 *   · 등록 0건이면 버튼 이름이 「시험지 없이 종료」 — 시험지를 만든다고 말하지 않는다
 *   · 등록이 1건이라도 있으면 서버에는 가드가 없다(등록 안 된 나머지는 조용히 빠진 채 시험지가 생긴다) —
 *     그래서 이 창이 같은 확인을 대신 한다(BE-20260925 §3 「설계 잔여」)
 * 세지 못하면(조회 실패) 서버가 다시 확인한다 — 409 셋을 사유로 가른다(classifyClose409).
 *
 * 폴더는 고르지 않는다 — `close` 가 `folder_id` 를 받지만 「폴더 편성 안 함」 방침이라 안 보낸다.
 * 만들어진 시험지는 무소속으로 서고, 시험지(/exam-papers)에서 옮길 수 있다.
 */

const STAGE_LINES = [
  { key: 'ready', label: '바로 등록 가능', how: null },
  { key: 'repromote', label: '고친 뒤 다시 등록할 것', how: '다시 등록하기 전에는 문제은행·시험지에 고치기 전 내용이 들어갑니다' },
  { key: 'rereview', label: '재검수 대기', how: '⑤에서 AI 결과를 승인해야 등록 대상이 됩니다' },
  { key: 'analysisPending', label: '분석 대기', how: '⑤에서 AI 분석을 돌려야 등록 대상이 됩니다' },
  { key: 'blocked', label: '등록 불가', how: '④에서 정답·플래그를 확인하세요' },
];

function StageLines({ counts, keys }) {
  const rows = STAGE_LINES.filter((l) => keys.includes(l.key) && Number(counts?.[l.key]) > 0);
  if (rows.length === 0) return null;
  return (
    <ul className="mt-1 space-y-0.5">
      {rows.map((l) => (
        <li key={l.key}>
          · <span className="font-medium">{l.label} {counts[l.key]}건</span>
          {l.how ? ` — ${l.how}` : ''}
        </li>
      ))}
    </ul>
  );
}

function ClosePaperModal({
  isOpen, onClose, projectId, projectName, stats, getAccessToken, onClosed, onOpenPromote = null,
}) {
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState(null);       // closeStageCounts 결과 — null 이면 아직 모르거나 세지 못함
  const [counting, setCounting] = useState(false);
  const [countNote, setCountNote] = useState(null);  // 세지 못했거나 일부만 읽었을 때 한 줄
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // 사유 없는 409(등록 0건)를 받은 뒤 — 창이 등록분이 있다고 알았는데 서버에는 없었다(은행에서 지워짐 등)
  const [serverNoPromoted, setServerNoPromoted] = useState(false);

  const loadCounts = useCallback(async () => {
    setCounting(true);
    setCountNote(null);
    try {
      const d = await fetchAllProjectProblems(projectId, getAccessToken);
      setCounts(closeStageCounts(d.items));
      if (d.truncated) setCountNote('문제 목록을 일부만 읽었습니다 — 아래 수가 실제보다 적을 수 있습니다(서버가 다시 확인합니다).');
    } catch (_) {
      // 오류 원문은 붙이지 않는다 — 공용 조회 유틸의 message 는 서버 응답 JSON 을 통째로 담는다
      setCounts(null);
      setCountNote('등록 상태를 확인하지 못했습니다 — 종료를 누르면 서버가 다시 확인합니다.');
    } finally {
      setCounting(false);
    }
  }, [projectId, getAccessToken]);

  useEffect(() => {
    if (!isOpen) return;
    setTitle(projectName || '');
    setError(null);
    setConfirmDiscard(false);
    setServerNoPromoted(false);
    setCounts(null);
    loadCounts();
    // 여는 순간 한 번만 센다 — 열려 있는 동안 폴링이 projectName 정체성을 바꿔도 다시 세지 않는다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const m = closeMode(counts, stats?.promoted ?? null);
  const promoted = counts ? counts.promoted : (stats?.promoted ?? null);
  const willMakePaper = serverNoPromoted ? false : m.willMakePaper;

  const doClose = async ({ force = false, discard = false } = {}) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // 제목은 늘 싣는다 — 두 번째 호출에서 빠지던 것(BE-20260925 §3 F12 보충). 시험지를 안 만들면 서버가 쓰지 않는다.
      const data = await closeProject(projectId, { title, force, discardUnpromoted: discard }, getAccessToken);
      if (data === null) return; // 401 → /signin 리다이렉트 중
      onClosed(data);
    } catch (e) {
      if (e.httpStatus === 409) {
        const c = classifyClose409(e);
        if (c.kind === 'unpromoted') {
          // 서버가 센 수로 창을 다시 세운다 — 창이 센 것과 달랐다(그 사이 누군가 승인·분석했다).
          // 세지 못했다는 안내도 거둔다 — 이제 수를 안다
          setCounts(c.counts);
          setCountNote(null);
          setServerNoPromoted(false);
          setConfirmDiscard(false);
        } else if (c.kind === 'no_promoted') {
          setServerNoPromoted(true);
          setConfirmDiscard(false);
        }
        setError(e.message || '프로젝트를 종료하지 못했습니다.');
      } else {
        setError(e.message || '프로젝트를 종료하지 못했습니다.');
      }
    } finally {
      setBusy(false);
    }
  };

  // 버리고 닫기 — 시험지를 만들지 않는 종료면 force + (검수·분석한 것이 있으면) discard_unpromoted
  const closeDiscarding = () => {
    const reviewed = Number(counts?.reviewed) || 0;
    if (willMakePaper) doClose();
    else doClose({ force: true, discard: reviewed > 0 });
  };

  const openPromote = () => {
    if (busy || !onOpenPromote) return;
    onOpenPromote();
  };

  // ── 바닥 버튼 ──
  let buttons;
  if (confirmDiscard) {
    buttons = (
      <>
        <Button variant="secondary" size="sm" onClick={() => setConfirmDiscard(false)} disabled={busy}>돌아가기</Button>
        <Button variant="danger" size="sm" onClick={closeDiscarding} loading={busy} disabled={busy}>
          등록하지 않고 종료
        </Button>
      </>
    );
  } else if (serverNoPromoted) {
    buttons = (
      <>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>취소</Button>
        <Button size="sm" onClick={() => doClose({ force: true, discard: false })} loading={busy} disabled={busy}>
          시험지 없이 종료
        </Button>
      </>
    );
  } else {
    const titleMissing = willMakePaper && !title.trim();
    buttons = (
      <>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>취소</Button>
        {(m.mode === 'register' || m.mode === 'pending') && (
          <Button variant="secondary" size="sm" onClick={() => setConfirmDiscard(true)} disabled={busy || counting}>
            등록하지 않고 종료
          </Button>
        )}
        {m.mode === 'register' && onOpenPromote && (
          <Button size="sm" onClick={openPromote} disabled={busy || counting}>
            {m.registerCount}건 등록 창 열기
          </Button>
        )}
        {m.mode === 'paper' && (
          <Button size="sm" onClick={() => doClose()} loading={busy} disabled={busy || counting || titleMissing}>
            시험지로 저장하고 종료
          </Button>
        )}
        {m.mode === 'empty' && (
          <Button size="sm" onClick={() => doClose({ force: true })} loading={busy} disabled={busy || counting}>
            시험지 없이 종료
          </Button>
        )}
        {m.mode === 'unknown' && (
          <Button size="sm" onClick={() => doClose()} loading={busy} disabled={busy || counting || titleMissing}>
            종료
          </Button>
        )}
      </>
    );
  }

  const footerNote = !confirmDiscard && !serverNoPromoted && willMakePaper && !title.trim()
    ? '시험지 이름을 넣어 주세요.'
    : '';

  const footer = (
    <div className="flex w-full items-center justify-between gap-2">
      <span className="min-w-0 flex-1 text-xs text-amber-700">{footerNote}</span>
      <div className="flex shrink-0 items-center gap-2">{buttons}</div>
    </div>
  );

  // 버리고 닫을 때 무엇을 잃는지 — 확인 칸과 안내가 같은 문장을 쓴다
  const loseSentence = willMakePaper
    ? `지금 종료하면 등록된 ${promoted}건만 시험지에 담기고, 나머지는 문제은행에 들어가지 않습니다. 시험지를 만든 종료는 되돌릴 수 없습니다.`
    : '지금 종료하면 이 문제들은 문제은행에 들어가지 않고 시험지도 만들어지지 않습니다.';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="프로젝트 종료"
      size="md"
      closeOnEsc={!busy}
      closeOnBackdropClick={false}
      footer={footer}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-700">
          등록한 문제로 시험지 한 장을 만들고 프로젝트를 종료합니다.
          종료하면 업로드·실행·매칭·분석·등록이 모두 차단되고 읽기 전용으로 바뀝니다.
        </p>

        {counting && (
          <p className="flex items-center gap-2 text-xs text-gray-500">
            <Loader2 className="size-3.5 animate-spin" /> 등록 상태를 확인하는 중…
          </p>
        )}
        {countNote && <p className="text-xs text-amber-700">{countNote}</p>}

        {/* 등록할 것이 남음 — 주 버튼이 ⑥ 의 등록 창을 연다 */}
        {!counting && !confirmDiscard && m.mode === 'register' && (
          <div className="rounded-xl bg-amber-50 p-3 text-xs leading-relaxed text-amber-800">
            <div className="font-medium">등록할 수 있는 문제가 남아 있습니다.</div>
            <StageLines counts={counts} keys={['ready', 'repromote', 'rereview', 'analysisPending', 'blocked']} />
            <p className="mt-1.5">{loseSentence}</p>
          </div>
        )}

        {/* 등록 0건 · 지금 바로 등록할 것도 없음 — 할 일만 말한다 */}
        {!counting && !confirmDiscard && m.mode === 'pending' && (
          <div className="rounded-xl bg-amber-50 p-3 text-xs leading-relaxed text-amber-800">
            <div className="font-medium">
              검수·분석한 {counts.reviewed}문제가 아직 등록되지 않았지만, 지금 바로 등록할 수 있는 문제는 없습니다.
            </div>
            <StageLines counts={counts} keys={['rereview', 'analysisPending', 'blocked']} />
            <p className="mt-1.5">{loseSentence}</p>
          </div>
        )}

        {/* 등록분이 있고 더 등록할 것은 없음 — 담기지 않는 것만 알린다 */}
        {!counting && !confirmDiscard && m.mode === 'paper' && counts.reviewed > 0 && (
          <div className="rounded-xl bg-amber-50 p-3 text-xs leading-relaxed text-amber-800">
            <div className="font-medium">등록되지 않은 {counts.reviewed}건은 시험지에 담기지 않습니다.</div>
            <StageLines counts={counts} keys={['rereview', 'analysisPending', 'blocked']} />
          </div>
        )}

        {/* 버리고 닫기 확인 — 무엇을 잃는지 한 번 더 */}
        {confirmDiscard && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs leading-relaxed text-red-700">
            <div className="font-medium">
              정말 등록하지 않고 종료할까요? 검수·분석한 {m.discardCount ?? 0}문제는 문제은행에 들어가지 않습니다.
            </div>
            <p className="mt-1">
              {willMakePaper
                ? `시험지에는 등록된 ${promoted}건만 담깁니다. 시험지를 만든 종료는 되돌릴 수 없습니다.`
                : '시험지도 만들어지지 않습니다. 필요하면 ⑦의 [종료 취소]로 되돌릴 수 있습니다.'}
            </p>
          </div>
        )}

        <div className="space-y-2 rounded-xl border border-gray-200 p-3">
          <div className="flex items-center gap-2">
            <span className="shrink-0 text-xs text-gray-500">시험지 이름</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={busy || !willMakePaper}
              className="min-w-0 flex-1 rounded-lg border px-2 py-1.5 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
            />
          </div>
          <p className="text-[11px] text-gray-400">
            {willMakePaper ? (
              <>
                {promoted != null
                  ? `등록된 ${promoted}건이 교재 순서(단원·번호)로 담깁니다.`
                  : '등록된 문제가 교재 순서(단원·번호)로 담깁니다.'}
                {' '}배점은 넣지 않습니다 — 문제에 「4점」 라벨로 남아 있습니다.
                폴더는 정하지 않으며, [시험지]에서 옮길 수 있습니다.
              </>
            ) : (
              '등록된 문제가 없어 시험지를 만들지 않습니다 — ⑥에서 등록하면 이 이름으로 시험지가 만들어집니다.'
            )}
          </p>
        </div>

        {error && <p className="text-xs text-danger">{error}</p>}
        {busy && (
          <p className="flex items-center gap-2 text-xs text-gray-500">
            <Loader2 className="size-3.5 animate-spin" />
            {willMakePaper ? '시험지를 만들고 종료하는 중…' : '종료하는 중…'}
          </p>
        )}
      </div>
    </Modal>
  );
}

export default ClosePaperModal;
